// ═══════════════════════════════════════════════════════
// TransitDeal — Module TypeScript Types
// ═══════════════════════════════════════════════════════

// ── Payments ───────────────────────────────────────────
export enum PaymentStatus   { Pending=1, Processing=2, Released=3, Failed=4, Refunded=5, OnHold=6 }
export enum CancellationInitiator { Customer=1, Carrier=2, Admin=3, System=4 }
export enum CancellationReason    { CustomerChanged=1, CarrierUnavailable=2, VehicleBreakdown=3,
  NoShowCustomer=4, NoShowCarrier=5, FraudSuspected=6, DuplicateJob=7, PricingDispute=8, Other=9 }
export enum RefundStatus    { Pending=1, Approved=2, Rejected=3, Processed=4 }
export enum ReferralStatus  { Active=1, Expired=2, Exhausted=3, Suspended=4, Pending=5 }

export interface PaymentDto {
  id: string; jobId: string; carrierId: string;
  grossAmount: number; commissionDeducted: number;
  cashbackDeducted: number; referralDeducted: number; netAmount: number;
  onlineAmount?: number; codAmount?: number;
  status: PaymentStatus; payoutMethod: number;
  createdAt: string; releasedAt?: string; failureReason?: string;
  jobReference?: string;
}
export interface EarningsDashboardDto {
  carrierId: string; totalLifetimeEarnings: number; monthlyEarnings: number;
  referralEarnings: number; pendingPayment: number;
  totalJobsCompleted: number; activeReferrals: number; averageJobValue: number;
  recentPayments: PaymentDto[]; topReferrals: ReferralSummaryDto[];
}
export interface AdminPaymentsQueueDto { totalCount: number; totalNetAmount: number; payments: PaymentDto[] }
export interface AdminReferralItemDto {
  id: string; referrerUserId: string; referrerUserName: string;
  referredUserId: string; referredUserName: string;
  status: ReferralStatus; totalJobsReferred: number; totalEarned: number;
  createdAt: string; expiresAt: string; isWithdrawn: boolean;
}
export interface AdminReferralOverviewDto {
  totalReferrals: number; activeReferrals: number; pendingReferrals: number;
  totalEarned: number; referrals: AdminReferralItemDto[];
}
export interface CancellationDto {
  id: string; jobId: string; initiator: CancellationInitiator; reason: CancellationReason;
  reasonDetail?: string; isWithinFreeWindow: boolean; penaltyAmountCharged: number; cancelledAt: string;
  refund?: RefundDto;
}
export interface CancellationPolicyDto {
  canCancelFree: boolean; penaltyAmount: number; policyExplanation: string; hoursUntilJob: number;
}
export interface RefundDto { id: string; jobId: string; amount: number; reason: string; status: RefundStatus; createdAt: string; processedAt?: string }
export interface ReferralSummaryDto {
  id: string; referredUserId: string; referredUserName: string; status: ReferralStatus;
  totalJobsReferred: number; totalEarned: number; createdAt: string; expiresAt: string;
  availableDate: string; daysRemaining: number; isWithdrawn: boolean; canWithdraw: boolean;
}
export interface ReferralDashboardDto {
  referrerUserId: string; yourReferralCode: string;
  totalReferrals: number; activeReferrals: number;
  pendingReferrals: number; supplierReferralLimit: number;
  totalEarned: number; monthlyEarned: number; todayEarned: number;
  referrals: ReferralSummaryDto[];
}
export interface CommissionSummaryDto {
  grossCommission: number; cashbackCost: number; referralCost: number; netCommission: number;
  vatLiability: number; vatRegistrationRequired: boolean; runningAnnualCommission: number; vatThreshold: number;
}

// ── Notifications ──────────────────────────────────────
export enum NotificationChannel { Email=1, Sms=2, Push=3, InApp=4 }
export enum NotificationStatus  { Pending=1, Sent=2, Delivered=3, Failed=4, Bounced=5, Suppressed=6 }
export enum NotificationEventType {
  JobApproved=2, QuoteReceived=4, QuoteAccepted=5, ContractGenerated=6,
  JobStarted=7, JobCompleted=8, JobCancelled=9,
  PaymentInitiated=10, PaymentReleased=11,
  DocumentExpiry30=20, DocumentExpiry15=24, DocumentExpiry7=21, DocumentExpiry1=25,
  DocumentExpired=22,
  DisputeOpened=40, DisputeResolved=42, ReferralEarning=50,
  JobAlertMatch=70, Outbid=71,
  VerificationComplete=72,
  InsuranceExpiry30=73, InsuranceExpiry15=74, InsuranceExpiry7=75, InsuranceExpiry1=76,
  DriverLoggedIn=90,
}

export interface NotificationDto {
  id: string; userId: string; eventType: NotificationEventType; channel: NotificationChannel;
  status: NotificationStatus; subject: string; body: string;
  jobId?: string; paymentId?: string; disputeId?: string;
  createdAt: string; sentAt?: string; isRead: boolean;
}
export interface NotificationPreference {
  id: string; userId: string; emailEnabled: boolean; smsEnabled: boolean;
  pushEnabled: boolean; marketingEmail: boolean; pushDeviceToken?: string; pushPlatform?: string;
}

// ── Disputes ───────────────────────────────────────────
export enum DisputeStatus   { Open=1, InReview=2, Proposed=3, Resolved=4, Escalated=5, Closed=6, Withdrawn=7 }
export enum DisputeCategory { ItemDamaged=1, ItemMissing=2, LateDelivery=3, CarrierNoShow=4,
  IncorrectDelivery=5, PaymentDisputed=6, FraudSuspected=7, VehicleRepairQuality=8,
  RelayHandoverFailed=9, Other=10 }
export enum DisputeResolutionType { FullRefundToCustomer=1, PartialRefundToCustomer=2,
  NoRefund=3, CarrierPenalty=4, MutualAgreement=5, EscalatedToLegal=6 }
export enum DisputeParty { Customer=1, Carrier=2, Workshop=3, Platform=4 }

export interface DisputeDto {
  id: string; jobId: string; raisedByUserId: string; raisedByParty: DisputeParty;
  category: DisputeCategory; title: string; description: string;
  status: DisputeStatus; referenceNumber: string;
  assignedAgentId?: string; firstResponseAt?: string; slaBreached: boolean;
  resolutionType?: DisputeResolutionType; resolutionNotes?: string; refundAmount?: number;
  resolvedAt?: string; isEscalated: boolean; createdAt: string;
  updates: DisputeUpdateDto[];
}
export interface DisputeUpdateDto {
  id: string; authorUserId: string; authorParty: DisputeParty;
  message: string; attachmentUrls?: string[]; createdAt: string;
}
export interface DisputeSummaryDto {
  openCount: number; inReviewCount: number; escalatedCount: number;
  overdueSlaCount: number; avgResolutionHours: number;
}

// ── Reporting ──────────────────────────────────────────
export interface PlatformKpiSnapshot {
  totalJobsAllTime: number; jobsThisMonth: number; jobsLastMonth: number; jobsMoMGrowthPct: number;
  jobsPendingReview: number; jobsInProgress: number;
  grossCommissionThisMonth: number; netCommissionThisMonth: number; grossCommissionLastMonth: number;
  revenueMoMGrowthPct: number; runningAnnualCommission: number;
  totalVerifiedCarriers: number; newCarriersThisMonth: number; activeCarriersThisMonth: number; carrierRetention90Day: number;
  relayJobsThisMonth: number; relayJobPct: number;
  pendingPaymentValue: number; avgPaymentReleaseHours: number;
  openDisputeCount: number; avgDisputeResolutionHours: number;
  activeReferrals: number; referralCostThisMonth: number;
  vatRegistrationRequired: boolean; vatThresholdPct: number;
}
export interface MonthlyKpiRow {
  year: number; month: number; monthLabel: string; jobs: number;
  grossCommission: number; netCommission: number; newDrivers: number;
  activeCarriers: number; relayJobs: number; disputes: number; refundsPaid: number; cancellations: number;
}
export interface DailyRevenueTick { date: string; grossCommission: number; netCommission: number; jobs: number }
export interface TopCarrierRow {
  carrierId: string; fullName: string; jobsCompleted: number; totalEarned: number;
  averageJobValue: number; averageRating: number; referralCount: number;
}
export interface GeographicJobTick { city: string; jobs: number; revenue: number }
export interface PaymentSpeedReport {
  avgHoursFromPodToRelease: number; pct48HourCompliance: number;
  paymentsReleasedThisPeriod: number; totalPaid: number; pendingCount: number; pendingValue: number;
}
export interface VatComplianceReport {
  year: number; runningAnnualGross: number; vatThreshold: number;
  thresholdPct: number; vatRegistrationRequired: boolean; estimatedVatLiability: number;
}
